import type { FontSizeCore, FontWeightCore, LineHeightCore } from "~/core";
import type {
  FontSize,
  FontWeight,
  LineHeight,
  UIKitProviderTheme,
} from "~/shared/ui-kit";

export const createTheme = (
  currentLang: LanguageCode,
  entry?: {
    fontSize?: FontSizeCore;
    lineHeight?: LineHeightCore;
    fontWeight?: FontWeightCore;
    fonFamily?: {
      main?: string;
      additional?: string;
    };
  },
): UIKitProviderTheme => {
  const fontSize: FontSize = {
    headline: {
      desktop: {
        large: entry?.fontSize?.desktop_large_headline ?? 48,
        medium: entry?.fontSize?.desktop_medium_headline ?? 42,
      },
      mobile: {
        large: entry?.fontSize?.mobile_large_headline ?? 24,
        medium: entry?.fontSize?.mobile_medium_headline ?? 22,
      },
    },
    title: {
      desktop: {
        large: entry?.fontSize?.desktop_large_title ?? 48,
        medium: entry?.fontSize?.desktop_medium_title ?? 36,
        small: entry?.fontSize?.desktop_small_title ?? 22,
        xsmall: entry?.fontSize?.desktop_xsmall_title ?? 16,
      },
      mobile: {
        large: entry?.fontSize?.mobile_large_title ?? 28,
        medium: entry?.fontSize?.mobile_medium_title ?? 22,
        small: entry?.fontSize?.mobile_small_title ?? 20,
        xsmall: entry?.fontSize?.mobile_xsmall_title ?? 14,
      },
    },
    body: {
      desktop: {
        large: entry?.fontSize?.desktop_large_body ?? 22,
        medium: entry?.fontSize?.desktop_medium_body ?? 18,
        small: entry?.fontSize?.desktop_small_body ?? 14,
      },
      mobile: {
        large: entry?.fontSize?.mobile_large_body ?? 16,
        medium: entry?.fontSize?.mobile_medium_body ?? 14,
        small: entry?.fontSize?.mobile_small_body ?? 12,
      },
    },
    button: {
      desktop: {
        large: entry?.fontSize?.desktop_large_button ?? 22,
        medium: entry?.fontSize?.desktop_medium_button ?? 20,
        small: entry?.fontSize?.desktop_small_button ?? 16,
        xsmall: entry?.fontSize?.desktop_xsmall_button ?? 14,
      },
      mobile: {
        large: entry?.fontSize?.mobile_large_button ?? 22,
        medium: entry?.fontSize?.mobile_medium_button ?? 20,
        small: entry?.fontSize?.mobile_small_button ?? 16,
        xsmall: entry?.fontSize?.mobile_xsmall_button ?? 14,
      },
    },
  };

  const lineHeight: LineHeight = {
    headline: {
      desktop: {
        large: entry?.lineHeight?.desktop_large_headline ?? 48,
        medium: entry?.lineHeight?.desktop_medium_headline ?? 42,
      },
      mobile: {
        large: entry?.lineHeight?.mobile_large_headline ?? 24,
        medium: entry?.lineHeight?.mobile_medium_headline ?? 22,
      },
    },
    title: {
      desktop: {
        large: entry?.lineHeight?.desktop_large_title ?? 48,
        medium: entry?.lineHeight?.desktop_medium_title ?? 36,
        small: entry?.lineHeight?.desktop_small_title ?? 22,
        xsmall: entry?.lineHeight?.desktop_xsmall_title ?? 16,
      },
      mobile: {
        large: entry?.lineHeight?.mobile_large_title ?? 28,
        medium: entry?.lineHeight?.mobile_medium_title ?? 22,
        small: entry?.lineHeight?.mobile_small_title ?? 20,
        xsmall: entry?.lineHeight?.mobile_xsmall_title ?? 14,
      },
    },
    body: {
      desktop: {
        large: entry?.lineHeight?.desktop_large_body ?? 22,
        medium: entry?.lineHeight?.desktop_medium_body ?? 18,
        small: entry?.lineHeight?.desktop_small_body ?? 14,
      },
      mobile: {
        large: entry?.lineHeight?.mobile_large_body ?? 16,
        medium: entry?.lineHeight?.mobile_medium_body ?? 14,
        small: entry?.lineHeight?.mobile_small_body ?? 14,
      },
    },
    button: {
      desktop: {
        large: entry?.lineHeight?.desktop_large_button ?? 22,
        medium: entry?.lineHeight?.desktop_medium_button ?? 20,
        small: entry?.lineHeight?.desktop_small_button ?? 16,
        xsmall: entry?.lineHeight?.desktop_xsmall_button ?? 16,
      },
      mobile: {
        large: entry?.lineHeight?.mobile_large_button ?? 22,
        medium: entry?.lineHeight?.mobile_medium_button ?? 20,
        small: entry?.lineHeight?.mobile_small_button ?? 16,
        xsmall: entry?.lineHeight?.mobile_xsmall_button ?? 16,
      },
    },
  };

  const fontWeight: FontWeight = {
    desktop: {
      superbold: entry?.fontWeight?.desktop_superbold ?? 900,
      bold: entry?.fontWeight?.desktop_bold ?? 700,
      semibold: entry?.fontWeight?.desktop_semibold ?? 600,
      normal: entry?.fontWeight?.desktop_normal ?? 400,
      light: entry?.fontWeight?.desktop_light ?? 100,
    },
    mobile: {
      superbold: entry?.fontWeight?.mobile_superbold ?? 900,
      bold: entry?.fontWeight?.mobile_bold ?? 700,
      semibold: entry?.fontWeight?.mobile_semibold ?? 600,
      normal: entry?.fontWeight?.mobile_normal ?? 400,
      light: entry?.fontWeight?.mobile_light ?? 100,
    },
  };

  return {
    global:
      currentLang === "zh"
        ? { lineBreak: "auto", wordBreak: "auto-phrase" }
        : { lineBreak: "normal", wordBreak: "auto-phrase" },
    fontSize,
    lineHeight,
    fontWeight,
    fontFamily: {
      main: `"${entry?.fonFamily?.main ?? "Main"}", sans-serif`,
      additional: `"${entry?.fonFamily?.additional ?? "Additional"}", sans-serif`,
    },
  };
};
